import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, IconButton, Paper
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#1b4332';
const AMBER  = '#b45309';

const HDR_SX = {
  fontWeight: 700,
  color: '#fff',
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${AMBER} 0%, #d97706 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)',
  py: 1.2,
  px: 0.8,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

const CELL_SX = { py: 0.9, px: 0.8, fontSize: '0.77rem', textAlign: 'center' };

function fmtNum(v, d = 3) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function fmtPct(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v) * 100;
  return isNaN(n) ? '—' : (n >= 0 ? '+' : '') + n.toFixed(2) + ' %';
}

function isGrandTotal(row) {
  return String(row.FOREST || '').trim().toUpperCase().startsWith('GRAND TOTAL');
}

// ─── Shared common columns (all summary tables share these) ──────────────────
const COMMON_COLS = [
  { label: 'LOGS',           key: 'LOG_COUNT',             fmt: v => fmtNum(v, 0) },
  { label: 'SECTIONS',       key: 'SECTION_COUNT',          fmt: v => fmtNum(v, 0) },
  { label: 'CUTTING CBM',    key: 'CUTTING_CBM',            fmt: v => fmtNum(v, 3) },
  { label: 'CBM BORD. (Ref)',key: 'CBM_BORDEREAU',          fmt: v => fmtNum(v, 3) },
  { label: 'CBM BORD. (Act)',key: 'CBM_BORDEREAU_ACT',      fmt: v => fmtNum(v, 3) },
  { label: 'GAIN/LOSS %',    key: 'SECTION_GAIN',           fmt: v => fmtPct(v), isGainLoss: true },
];

// ─── Generic summary table ───────────────────────────────────────────────────
function SummaryTable({ rows, groupCols }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  const cols = [...groupCols, ...COMMON_COLS];
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#1a3a2a' : (i % 2 === 0 ? '#fff' : '#fefce8'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                {cols.map(col => {
                  const val = col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                  const isGain = col.isGainLoss && !grand && row[col.key] != null;
                  const gainVal = isGain ? parseFloat(row[col.key]) : null;
                  return (
                    <TableCell key={col.label} sx={{
                      ...CELL_SX,
                      fontWeight: grand ? 700 : 500,
                      color: grand
                        ? '#ffffff'
                        : isGain
                          ? (gainVal >= 0 ? '#166534' : '#991b1b')
                          : 'inherit',
                    }}>
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Detail table (RS[4] — log-level detail) ─────────────────────────────────
function DetailLogTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  const cols = [
    { label: 'FOREST',       key: 'FOREST'          },
    { label: 'CHALAN NO',    key: 'CHALAN'           },
    { label: 'LOG NO',       key: 'LogNo'            },
    { label: 'DATE',         key: 'DATE_OF_CUTTING', fmt: v => v ? String(v).substring(0, 10) : '—' },
    { label: 'QUALITY',      key: 'Quality'          },
    { label: 'SIZE',         key: 'size1'            },
    { label: 'ESSENCE',      key: 'ESSENCE'          },
    { label: 'LOGS',         key: 'LOG_COUNT',       fmt: v => fmtNum(v, 0) },
    { label: 'SECTIONS',     key: 'SECTION_COUNT',   fmt: v => fmtNum(v, 0) },
    { label: 'CUT CBM',      key: 'CUTTING_CBM',     fmt: v => fmtNum(v, 3) },
    { label: 'CBM (Ref)',    key: 'CBM_BORDEREAU',   fmt: v => fmtNum(v, 3) },
    { label: 'CBM (Act)',    key: 'CBM_BORDEREAU_ACT',fmt: v => fmtNum(v, 3) },
    { label: 'GAIN/LOSS %',  key: 'SECTION_GAIN',    fmt: v => fmtPct(v), isGainLoss: true },
  ];
  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1100 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#1a3a2a' : (i % 2 === 0 ? '#fff' : '#fefce8'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                {cols.map(col => {
                  const val = col.fmt ? col.fmt(row[col.key]) : (row[col.key] ?? '—');
                  const isGain = col.isGainLoss && !grand && row[col.key] != null;
                  const gainVal = isGain ? parseFloat(row[col.key]) : null;
                  return (
                    <TableCell key={col.label} sx={{
                      ...CELL_SX,
                      fontWeight: grand ? 700 : 500,
                      color: grand ? '#ffffff' : isGain ? (gainVal >= 0 ? '#166534' : '#991b1b') : 'inherit',
                    }}>
                      {val}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Full raw detail (RS[5]) ──────────────────────────────────────────────────
function RawDetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data found</Alert>;
  const cols = [
    { label: '#',            key: '__idx'           },
    { label: 'FOREST',       key: 'FOREST'          },
    { label: 'LOG NO',       key: 'LogNo'           },
    { label: 'DATE',         key: 'DATE_OF_CUTTING', fmt: v => v ? String(v).substring(0, 10) : '—' },
    { label: 'QUALITY',      key: 'Quality'         },
    { label: 'SIZE',         key: 'size1'           },
    { label: 'ESSENCE',      key: 'ESSENCE'         },
    { label: 'CBM',          key: 'CBM',             fmt: v => fmtNum(v, 3) },
    { label: 'CBM (Ref)',    key: 'CBM_BORDEREAU_F', fmt: v => fmtNum(v, 3) },
    { label: 'CBM (Act)',    key: 'CBM_BORDEREAU_ACT',fmt: v => fmtNum(v, 3) },
    { label: 'RATE',         key: 'RATE',            fmt: v => fmtNum(v, 2) },
    { label: 'FINAL RATE',   key: 'FINAL_RATE',      fmt: v => fmtNum(v, 2) },
    { label: 'GAIN/LOSS %',  key: 'SECTION_GAIN',    fmt: v => fmtPct(v), isGainLoss: true },
  ];
  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1100 }}>
        <TableHead>
          <TableRow>
            {cols.map(c => <TableCell key={c.label} sx={HDR_SX}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#fefce8',
              '&:hover': { filter: 'brightness(0.97)' },
            }}>
              {cols.map(col => {
                const raw = col.key === '__idx' ? i + 1 : row[col.key];
                const val = col.fmt ? col.fmt(raw) : (raw ?? '—');
                const isGain = col.isGainLoss && raw != null;
                const gainVal = isGain ? parseFloat(raw) : null;
                return (
                  <TableCell key={col.label} sx={{
                    ...CELL_SX,
                    color: isGain ? (gainVal >= 0 ? '#166534' : '#991b1b') : 'inherit',
                  }}>
                    {val}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Stat chip ────────────────────────────────────────────────────────────────
function StatChip({ label, value, color = AMBER }) {
  return (
    <Paper elevation={0} sx={{
      px: 2, py: 1.5, borderRadius: 2,
      border: `1.5px solid ${color}30`,
      background: `${color}08`,
      textAlign: 'center',
      minWidth: 120,
    }}>
      <Typography sx={{ fontSize: '0.68rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color }}>
        {value}
      </Typography>
    </Paper>
  );
}

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = [
  { label: 'BY FOREST',             rsIdx: 0 },
  { label: 'BY FOREST + QUALITY',   rsIdx: 1 },
  { label: 'BY FOREST + SIZE',      rsIdx: 2 }, // RS[2] = FOREST+QUALITY+SIZE, RS[3] = FOREST+SIZE
  { label: 'LOG DETAIL',            rsIdx: 4 },
  { label: 'RAW DETAIL',            rsIdx: 5 },
];

// ─── Main view ────────────────────────────────────────────────────────────────
const LogCuttingReportView = ({ onBackClick, user }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate,   setToDate]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [data,     setData]     = useState(null);
  const [tab,      setTab]      = useState(0);

  const fetchData = async () => {
    if (!fromDate || !toDate) { setError('Please select both From and To dates'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/logcuttingreport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: fromDate.format('YYYY-MM-DD'),
          to_date:   toDate.format('YYYY-MM-DD'),
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected date range.');
        return;
      }
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const sheets = [
      { label: 'By Forest',              rows: data[0] },
      { label: 'By Forest+Quality',      rows: data[1] },
      { label: 'By Forest+Quality+Size', rows: data[2] },
      { label: 'By Forest+Size',         rows: data[3] },
      { label: 'Log Detail',             rows: data[4] },
      { label: 'Raw Detail',             rows: data[5] },
    ];
    sheets.forEach(({ label, rows }) => {
      if (rows?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), label);
    });
    XLSX.writeFile(wb, `LogCutting_${fromDate.format('YYYY-MM-DD')}_to_${toDate.format('YYYY-MM-DD')}.xlsx`);
  };

  // Grand total from RS[0]
  const grandTotal = data?.[0]?.find(r => isGrandTotal(r));

  const renderTable = () => {
    if (!data) return null;
    switch (tab) {
      case 0: return <SummaryTable rows={data[0]} groupCols={[
        { label: 'FOREST',  key: 'FOREST'  },
        { label: 'ESSENCE', key: 'ESSENCE' },
      ]} />;
      case 1: return <SummaryTable rows={data[1]} groupCols={[
        { label: 'FOREST',  key: 'FOREST'  },
        { label: 'QUALITY', key: 'Quality' },
        { label: 'ESSENCE', key: 'ESSENCE' },
      ]} />;
      case 2: return <SummaryTable rows={data[3]} groupCols={[
        { label: 'FOREST',  key: 'FOREST'  },
        { label: 'SIZE',    key: 'size1'   },
        { label: 'ESSENCE', key: 'ESSENCE' },
      ]} />;
      case 3: return <DetailLogTable rows={data[4]} />;
      case 4: return <RawDetailTable rows={data[5]} />;
      default: return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      {/* Header card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(180,83,9,0.1)', border: '1px solid #fef3c7' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: AMBER }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, #1a3a2a 0%, #2d5a3d 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <ContentCutIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Log Cutting Summary Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Cutting CBM, Bordereau CBM, section gain/loss — by date range</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={setToDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <Button
                variant="contained" size="large" startIcon={<SearchIcon />}
                onClick={fetchData} disabled={loading}
                sx={{
                  minWidth: 140,
                  backgroundColor: GREEN,
                  '&:hover': { backgroundColor: '#0f2419' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>

          {/* Quick stats */}
          {grandTotal && (
            <Box display="flex" flexWrap="wrap" gap={1.5} mt={2}>
              <StatChip label="Total Logs"    value={fmtNum(grandTotal.LOG_COUNT, 0)}    color={GREEN} />
              <StatChip label="Sections"      value={fmtNum(grandTotal.SECTION_COUNT, 0)} color={GREEN} />
              <StatChip label="Cutting CBM"   value={fmtNum(grandTotal.CUTTING_CBM, 3)}  color={AMBER} />
              <StatChip label="CBM Bord.(Ref)"value={fmtNum(grandTotal.CBM_BORDEREAU, 3)}color={AMBER} />
              <StatChip label="Gain/Loss"     value={fmtPct(grandTotal.SECTION_GAIN)}
                color={parseFloat(grandTotal.SECTION_GAIN) >= 0 ? '#166534' : '#991b1b'} />
            </Box>
          )}
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #fef3c7' }}>
          <Box sx={{
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, pt: 1,
          }}>
            <Tabs
              value={tab} onChange={(_, v) => setTab(v)}
              textColor="inherit" variant="scrollable" scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: AMBER },
                '& .MuiTabs-indicator': { backgroundColor: AMBER },
              }}
            >
              {TABS.map((t, i) => <Tab key={i} label={t.label} />)}
            </Tabs>
            <Button
              size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}
            >
              Excel
            </Button>
          </Box>

          {renderTable()}
        </Card>
      )}

      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label="Send Log Cutting Summary to WhatsApp"
            apiUrl={
              fromDate && toDate
                ? `${API_URL}/api/logcutting/send?from_date=${fromDate.format('YYYY-MM-DD')}&to_date=${toDate.format('YYYY-MM-DD')}`
                : null
            }
            disabled={!fromDate || !toDate}
          />
        </Box>
      )}
    </Container>
  );
};

export default LogCuttingReportView;
